// The DAW's storage, in this browser (IndexedDB): projects, the recordings their audio clips play
// (with each one's waveform, once it's been heard), and the ideas gathered from around the site to use
// in them ("Send to DAW" on a map rhythm, a reel, a studio track...). Nothing leaves the browser.
import { isRef, scorePrint } from "../stats/refs";

const DB = "truechops-daw", VERSION = 1, STORES = ["projects", "audio", "gathered"];
export const GATHERED_EVENT = "truechops:daw-gathered";
let opening = null;

function open() {
  if (!opening) opening = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB, VERSION);
    request.onupgradeneeded = () => { for (const name of STORES) if (!request.result.objectStoreNames.contains(name)) request.result.createObjectStore(name); };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => { opening = null; reject(request.error || new Error("This browser can't keep DAW projects.")); };
  });
  return opening;
}
async function run(store, mode, work) {
  const db = await open();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(store, mode), request = work(transaction.objectStore(store));
    transaction.oncomplete = () => resolve(request?.result);
    transaction.onerror = transaction.onabort = () => reject(transaction.error || new Error("The DAW couldn't save that."));
  });
}
const all = (store) => run(store, "readonly", (objects) => objects.getAll());
const key = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export const projects = {
  async list() { return (await all("projects")).sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt))); },
  get: (id) => run("projects", "readonly", (objects) => objects.get(id)),
  put: (project) => run("projects", "readwrite", (objects) => objects.put(project, project.id)),
  remove: (id) => run("projects", "readwrite", (objects) => objects.delete(id)),
};

// A recording: { blob, name, seconds, peaks (its waveform, when known) }, by its key.
export const recordings = {
  get: (id) => run("audio", "readonly", (objects) => objects.get(id)),
  async add(blob, about = {}) { const id = key(); await run("audio", "readwrite", (objects) => objects.put({ blob, name: about.name || "Recording", seconds: about.seconds || 0, peaks: about.peaks || null }, id)); return id; },
  async update(id, change) { const current = await recordings.get(id); if (current) await run("audio", "readwrite", (objects) => objects.put({ ...current, ...change }, id)); },
};

// Gathered ideas: { id, kind: "rhythm" (score, tempo) | "audio" (audio: a recording's key, seconds),
// title, from (where it was found), href (its page), addedAt }, newest first.
export const gathered = {
  async list() { return (await all("gathered")).sort((a, b) => String(b.addedAt).localeCompare(String(a.addedAt))); },
  remove: async (id) => { await run("gathered", "readwrite", (objects) => objects.delete(id)); announce(); },
};
function announce() { try { window.dispatchEvent(new Event(GATHERED_EVENT)); } catch { /* nothing listening */ } }

// An idea from anywhere on the site kept for the DAW. A rhythm: { title, score, tempo }; audio: { title,
// url } (fetched now: studio tracks don't last) or { title, blob }. `ref` is the idea's own (src/lib/
// stats/refs.js), so the clips made from it remember where they came from. Returns the gathered item.
export async function sendToDaw({ title, score = null, tempo = null, url = null, blob = null, from = "", href = "", ref = null }) {
  const item = { id: key(), title: String(title || "Idea").slice(0, 80), from: String(from).slice(0, 60), href: String(href).slice(0, 300), addedAt: new Date().toISOString(),
    ...(isRef(ref) ? { source: { ref, name: String(title || "Idea").slice(0, 80), ...(score ? { print: scorePrint(score) } : {}) } } : {}) };
  if (score) Object.assign(item, { kind: "rhythm", score, tempo: Number(tempo) || 100 });
  else {
    const audio = blob || await (await fetch(url, { credentials: "same-origin" }).then((response) => { if (!response.ok) throw new Error("That track couldn't be fetched."); return response; })).blob();
    Object.assign(item, { kind: "audio", audio: await recordings.add(audio, { name: item.title }) });
  }
  await run("gathered", "readwrite", (objects) => objects.put(item, item.id));
  announce();
  return item;
}
